import React, { useState, useEffect, useMemo, useCallback } from "react";

const API_MESSAGE_LIST = "/api/message-list.php";
const API_MESSAGE_SEND = "/api/message-send.php";
const API_USERS_LIST = "/api/users-list.php";

function safeNumber(val) {
  const n = Number(val);
  return isNaN(n) ? 0 : n;
}

function getInitial(name) {
  return (name || "W").charAt(0).toUpperCase();
}

export default function UnifiedMessagesModule({
  currentUser = {},
  userRole = "publisher", // 'publisher' | 'employee' | 'admin' | 'scout' | 'editor'
  contacts = [],
  messages = [],
  reloadMessages,
  theme = "dark",
}) {
  const isDark = theme === "dark";
  const [internalMessages, setInternalMessages] = useState([]);
  const [internalContacts, setInternalContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [selectedContactKey, setSelectedContactKey] = useState("");
  const [selectedTopicKey, setSelectedTopicKey] = useState("");
  const [composerRole, setComposerRole] = useState(userRole === "admin" ? "admin" : "standard");
  const [activeContactFilter, setActiveContactFilter] = useState("all"); // 'all' | 'unread' | 'vip'
  const [activeTopicFilter, setActiveTopicFilter] = useState("all"); // 'all' | 'unread'
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [modalReceiverId, setModalReceiverId] = useState("");
  const [modalCategory, setModalCategory] = useState("💎 ELMAS & HEDEF");
  const [modalSubject, setModalSubject] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(API_MESSAGE_LIST, { cache: "no-store" });
      const data = await res.json();
      if (data.success && Array.isArray(data.messages)) {
        setInternalMessages(data.messages);
      }
    } catch (err) {
      console.warn("Mesaj listesi yüklenemedi:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (contacts && contacts.length > 0) {
      setInternalContacts(contacts);
    } else {
      fetch(API_USERS_LIST)
        .then((r) => r.json())
        .then((d) => {
          if (d.success && Array.isArray(d.users)) {
            setInternalContacts(d.users);
          }
        })
        .catch(() => {});
    }
  }, [contacts]);

  useEffect(() => {
    if (messages && messages.length > 0) {
      setInternalMessages(messages);
    } else {
      fetchMessages();
    }
  }, [messages, fetchMessages]);

  const activeMessages = messages.length > 0 ? messages : internalMessages;
  const activeContactsList = useMemo(() => {
    const rawList = contacts.length > 0 ? contacts : internalContacts;
    
    // 🌟 ROL BAZLI ALICI LİSTESİ MATRIXİ (Kullanıcı İsteğine Birebir Uygun)
    if (userRole === "publisher") {
      // 1. Yayıncı: Yöneticisi, Üst Yönetim ve Teknik Destek seçebilir
      const systemRecipients = [
        {
          id: "mgr_support",
          username: "yoneticim",
          fullName: currentUser?.representative ? `👨‍💼 Yöneticim (${currentUser.representative})` : "👨‍💼 Ajans Yöneticim & Temsilcim",
          role: "manager",
          type: "manager"
        },
        {
          id: "admin_support",
          username: "ust_yonetim",
          fullName: "👑 Üst Yönetim (Master Admin / Ajans)",
          role: "admin",
          type: "admin"
        },
        {
          id: "tech_support",
          username: "teknik_destek",
          fullName: "🛠️ Teknik Destek & Canlı Yayın Yardım",
          role: "tech",
          type: "support"
        }
      ];

      // Sunucudan gelen yöneticileri ve adminleri de ekle
      const dbStaff = rawList.filter(u => 
        (u.role === "manager" || u.role === "employee" || u.role === "admin" || u.powerBIRole === "manager") &&
        String(u.id) !== String(currentUser?.id)
      ).map(u => ({
        id: String(u.id),
        username: u.username,
        fullName: `👤 ${u.fullName || u.username} (${u.role === 'admin' ? 'Üst Yönetim' : 'Yönetici'})`,
        role: u.role || "manager"
      }));

      return [...systemRecipients, ...dbStaff];

    } else if (userRole === "employee" || userRole === "manager") {
      // 2. Yönetici: Üst Yönetim, Yayıncıları ve Teknik Destek seçebilir
      const systemRecipients = [
        {
          id: "admin_support",
          username: "ust_yonetim",
          fullName: "👑 Üst Yönetim (Master Admin)",
          role: "admin",
          type: "admin"
        },
        {
          id: "tech_support",
          username: "teknik_destek",
          fullName: "🛠️ Teknik Destek Birimi",
          role: "tech",
          type: "support"
        }
      ];

      // Yayıncıları ekle
      const dbPublishers = rawList.filter(u => 
        (u.role === "publisher" || !u.role || u.role === "user") &&
        String(u.id) !== String(currentUser?.id)
      ).map(u => ({
        id: String(u.id),
        username: u.username,
        fullName: `🎙️ ${u.fullName || u.username} (@${u.username})`,
        role: "publisher"
      }));

      return [...systemRecipients, ...dbPublishers];

    } else {
      // 3. Admin: Tamamı seçebilir (Tüm Yayıncılar, Tüm Yöneticiler, Teknik Destek, Editörler, Scoutlar)
      const systemRecipients = [
        {
          id: "all_publishers",
          username: "tum_yayincilar",
          fullName: "📢 TÜM YAYINCILAR (Toplu Ajans Bildirisi)",
          role: "publisher"
        },
        {
          id: "all_managers",
          username: "tum_yoneticiler",
          fullName: "📢 TÜM YÖNETİCİLER (Toplu Yönetim Bildirisi)",
          role: "manager"
        },
        {
          id: "tech_support",
          username: "teknik_destek",
          fullName: "🛠️ Teknik Destek Birimi",
          role: "tech"
        }
      ];

      const allDbUsers = rawList.filter(u => String(u.id) !== String(currentUser?.id)).map(u => ({
        id: String(u.id),
        username: u.username,
        fullName: `${u.role === 'publisher' ? '🎙️' : u.role === 'manager' ? '👨‍💼' : '👤'} ${u.fullName || u.username} (@${u.username} - ${u.role || 'Yayıncı'})`,
        role: u.role || "user"
      }));

      return [...systemRecipients, ...allDbUsers];
    }
  }, [contacts, internalContacts, userRole, currentUser]);

  function formatMessageDate(value) {
    if (!value) return "-";
    try {
      const date = new Date(String(value).replace(" ", "T"));
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const timeStr = date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
      if (isToday) return `Bugün ${timeStr}`;
      return date.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  }

  function cleanSubject(subject) {
    return String(subject || "Genel Konu")
      .replace(/^yanıt:s*/i, "")
      .replace(/^re:s*/i, "")
      .trim();
  }

  const getContactKeyFromMessage = useCallback((msg) => {
    const senderId = String(msg.sender_id || "");
    const receiverId = String(msg.receiver_id || "");
    const myId = String(currentUser?.id || "");

    if (senderId && senderId !== myId) return `id:${senderId}`;
    if (receiverId && receiverId !== myId) return `id:${receiverId}`;
    if (msg.senderUsername && msg.senderUsername !== currentUser?.username) return `username:${msg.senderUsername}`;
    if (msg.receiverUsername && msg.receiverUsername !== currentUser?.username) return `username:${msg.receiverUsername}`;
    return "support";
  }, [currentUser?.id, currentUser?.username]);

  const findContactByKey = useCallback((key) => {
    if (!key) return null;
    if (key.startsWith("id:")) {
      const id = key.replace("id:", "");
      return activeContactsList.find((c) => String(c.id) === String(id)) || null;
    }
    if (key.startsWith("username:")) {
      const u = key.replace("username:", "");
      return activeContactsList.find((c) => String(c.username) === String(u)) || null;
    }
    return null;
  }, [activeContactsList]);

  const groupedContacts = useMemo(() => {
    const map = {};

    activeContactsList.forEach((c) => {
      if (currentUser?.id && String(c.id) === String(currentUser.id)) return;
      const key = `id:${c.id}`;
      map[key] = {
        key,
        contact: c,
        id: c.id,
        name: c.fullName || c.name || c.username || "Kullanıcı",
        username: c.username || "-",
        hiddenId: c.hiddenId || "-",
        role: c.role || "publisher",
        tier: c.powerBIRole || "Kademe 1",
        diamonds: safeNumber(c.currentDiamonds ?? c.diamonds ?? 0),
        targetDiamonds: safeNumber(c.targetDiamonds || c.target_diamonds || 0),
        status: c.status || "Aktif",
        unreadCount: 0,
        lastDate: c.createdAt || "",
        topics: {},
      };
    });

    activeMessages.forEach((msg) => {
      const cKey = getContactKeyFromMessage(msg);
      const matched = findContactByKey(cKey);
      const subject = cleanSubject(msg.subject);
      const topicKey = subject.toLowerCase();

      if (!map[cKey]) {
        map[cKey] = {
          key: cKey,
          contact: matched,
          id: matched?.id || cKey.replace("id:", ""),
          name: matched?.fullName || msg.senderName || msg.senderUsername || msg.receiverName || "Destek & Yönetim",
          username: matched?.username || msg.senderUsername || msg.receiverUsername || "-",
          hiddenId: matched?.hiddenId || "-",
          role: matched?.role || msg.sender_role || "manager",
          tier: matched?.powerBIRole || "Kademe 1",
          diamonds: safeNumber(matched?.currentDiamonds ?? matched?.diamonds ?? 0),
          targetDiamonds: safeNumber(matched?.targetDiamonds || matched?.target_diamonds || 0),
          status: matched?.status || "Aktif",
          unreadCount: 0,
          lastDate: msg.created_at,
          topics: {},
        };
      }

      let category = "💎 ELMAS & HEDEF";
      const subLower = subject.toLowerCase();
      if (subLower.includes("kargo") || subLower.includes("ödül") || subLower.includes("hediye") || subLower.includes("plaket")) {
        category = "📦 ÖDÜL & KARGO";
      } else if (subLower.includes("kurgu") || subLower.includes("video") || subLower.includes("edit") || subLower.includes("reels")) {
        category = "🎬 VİDEO KURGU & EDİT";
      } else if (subLower.includes("şifre") || subLower.includes("destek") || subLower.includes("ayar") || subLower.includes("teknik")) {
        category = "⚙️ TEKNİK DESTEK";
      } else if (subLower.includes("pk") || subLower.includes("turnuva") || subLower.includes("etkinlik")) {
        category = "⚔️ PK & ETKİNLİK";
      }

      if (!map[cKey].topics[topicKey]) {
        map[cKey].topics[topicKey] = {
          key: topicKey,
          subject,
          category,
          status: "Yanıt Bekliyor",
          messages: [],
          unreadCount: 0,
          lastDate: msg.created_at,
        };
      }

      map[cKey].topics[topicKey].messages.push(msg);

      if (msg.status === "unread" && Number(msg.receiver_id) === Number(currentUser?.id)) {
        map[cKey].unreadCount += 1;
        map[cKey].topics[topicKey].unreadCount += 1;
      }

      if (new Date(msg.created_at) > new Date(map[cKey].lastDate || 0)) {
        map[cKey].lastDate = msg.created_at;
      }
      if (new Date(msg.created_at) > new Date(map[cKey].topics[topicKey].lastDate || 0)) {
        map[cKey].topics[topicKey].lastDate = msg.created_at;
      }
    });

    return Object.values(map)
      .map((cGroup) => {
        const topicsList = Object.values(cGroup.topics).sort(
          (a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0)
        );

        topicsList.forEach((top) => {
          const lastMsg = top.messages[top.messages.length - 1];
          if (lastMsg && Number(lastMsg.sender_id) === Number(currentUser?.id)) {
            top.status = "Yanıtlandı";
          } else {
            top.status = top.unreadCount > 0 ? "Yanıt Bekliyor" : "İşlemde";
          }
        });

        return {
          ...cGroup,
          topics: topicsList,
        };
      })
      .sort((a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0));
  }, [activeContactsList, activeMessages, currentUser?.id, getContactKeyFromMessage, findContactByKey]);

  const filteredContacts = useMemo(() => {
    return groupedContacts.filter((c) => {
      const matchSearch =
        !searchQuery ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(c.hiddenId).includes(searchQuery) ||
        c.topics.some((t) => t.subject.toLowerCase().includes(searchQuery.toLowerCase()));

      if (!matchSearch) return false;
      if (activeContactFilter === "unread") return c.unreadCount > 0;
      if (activeContactFilter === "vip") return ["Kademe 4", "Kademe 5", "Kademe 6", "Kademe 7", "Kademe 8", "Kademe 9", "Kademe 10"].includes(c.tier);
      return true;
    });
  }, [groupedContacts, searchQuery, activeContactFilter]);

  useEffect(() => {
    if (!selectedContactKey && filteredContacts.length > 0) {
      setSelectedContactKey(filteredContacts[0].key);
    }
  }, [filteredContacts, selectedContactKey]);

  const selectedContactGroup = useMemo(() => {
    return groupedContacts.find((c) => c.key === selectedContactKey) || filteredContacts[0] || null;
  }, [groupedContacts, selectedContactKey, filteredContacts]);

  useEffect(() => {
    if (selectedContactGroup && selectedContactGroup.topics.length > 0) {
      if (!selectedTopicKey || !selectedContactGroup.topics.some((t) => t.key === selectedTopicKey)) {
        setSelectedTopicKey(selectedContactGroup.topics[0].key);
      }
    } else {
      setSelectedTopicKey("");
    }
  }, [selectedContactGroup, selectedTopicKey]);

  const selectedTopic = useMemo(() => {
    if (!selectedContactGroup) return null;
    return selectedContactGroup.topics.find((t) => t.key === selectedTopicKey) || selectedContactGroup.topics[0] || null;
  }, [selectedContactGroup, selectedTopicKey]);

  const threadMessages = useMemo(() => {
    if (!selectedTopic) return [];
    return [...selectedTopic.messages].sort(
      (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
    );
  }, [selectedTopic]);

  const filteredTopics = useMemo(() => {
    if (!selectedContactGroup) return [];
    if (activeTopicFilter === "unread") {
      return selectedContactGroup.topics.filter((t) => t.unreadCount > 0);
    }
    return selectedContactGroup.topics;
  }, [selectedContactGroup, activeTopicFilter]);

  async function handleSendReply() {
    if (!selectedContactGroup) {
      alert("Lütfen sol listeden bir kişi veya konu seçiniz.");
      return;
    }
    if (!replyText.trim()) {
      alert("Mesaj alanı boş bırakılamaz.");
      return;
    }

    let finalMessage = replyText.trim();
    if (composerRole === "admin" && !finalMessage.startsWith("👑")) {
      finalMessage = `👑 [AJANS YÖNETİMİ MÜDAHALESİ]: ${finalMessage}`;
    } else if (composerRole === "internal" && !finalMessage.startsWith("🔒")) {
      finalMessage = `🔒 [DAHİLİ NOT - YÖNETİM]: ${finalMessage}`;
    }

    const lastMessage = threadMessages[threadMessages.length - 1];
    const myId = Number(currentUser?.id || 1);
    const receiverId = lastMessage
      ? Number(lastMessage.sender_id) === myId ? lastMessage.receiver_id : lastMessage.sender_id
      : selectedContactGroup.id;

    try {
      setIsSending(true);
      const res = await fetch(API_MESSAGE_SEND, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender_id: myId,
          sender_role: userRole,
          receiver_id: receiverId,
          subject: selectedTopic?.subject || "Genel Konu",
          message: finalMessage,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Mesaj gönderilemedi.");
        return;
      }

      setReplyText("");
      if (reloadMessages) reloadMessages();
      else fetchMessages();
    } catch (err) {
      console.error(err);
      alert("Mesaj gönderilirken bağlantı hatası oluştu.");
    } finally {
      setIsSending(false);
    }
  }

  async function handleCreateNewTopic() {
    if (!modalReceiverId) {
      alert("Lütfen bir alıcı seçiniz.");
      return;
    }
    if (!modalSubject.trim() || !modalMessage.trim()) {
      alert("Konu başlığı ve başlangıç mesajı zorunludur.");
      return;
    }

    try {
      setIsSending(true);
      const res = await fetch(API_MESSAGE_SEND, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender_id: currentUser?.id || 1,
          sender_role: userRole,
          receiver_id: modalReceiverId,
          subject: `[${modalCategory}] ${modalSubject.trim()}`,
          message: modalMessage.trim(),
        }),
      });

      const data = await res.json();
      if (!data.success) {
        alert(data.message || "Yeni konu başlatılamadı.");
        return;
      }

      setModalSubject("");
      setModalMessage("");
      setShowNewTopicModal(false);
      if (reloadMessages) reloadMessages();
      else fetchMessages();
    } catch (err) {
      console.error(err);
      alert("Konu oluşturulamadı.");
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div
      className={`overflow-hidden rounded-[1.8rem] border transition-colors duration-200 ${
        isDark
          ? "border-white/10 bg-[#0c0c0e] text-white shadow-[0_20px_60px_rgba(0,0,0,.6)]"
          : "border-slate-300 bg-white text-slate-900 shadow-xl"
      }`}
    >
      {/* 🚀 ÜST AKSİYON & ARAMA ŞERİDİ */}
      <div
        className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5 ${
          isDark ? "border-white/10 bg-[#121215]" : "border-slate-200 bg-slate-50"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => {
              setModalReceiverId(selectedContactGroup?.id || activeContactsList[0]?.id || "");
              setShowNewTopicModal(true);
            }}
            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-2 text-xs font-black text-black shadow-lg transition active:scale-95 hover:brightness-110"
          >
            <span className="text-sm">＋</span>
            <span>Yeni Konu / Talep Aç</span>
          </button>

          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("unifiedReplyInput");
              if (el) el.focus();
            }}
            className={`flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition ${
              isDark
                ? "border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08] hover:text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            }`}
          >
            <span>↩</span>
            <span>Yanıtla</span>
          </button>
        </div>

        <div className="relative min-w-[260px] flex-1 max-w-md">
          <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-xs ${isDark ? "text-white/40" : "text-slate-400"}`}>
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Kişi, ID veya Konu ara... (Ctrl+E)"
            className={`w-full rounded-xl border py-2 pl-9 pr-3 text-xs outline-none transition ${
              isDark
                ? "border-white/10 bg-black/50 text-white placeholder-white/40 focus:border-yellow-500/50"
                : "border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-yellow-500"
            }`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/50 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 🌟 3 KOLONLU MESAJLAŞMA GÖVDESİ */}
      <div
        className={`grid min-h-[660px] max-h-[820px] grid-cols-1 border-b xl:grid-cols-[290px_330px_1fr] ${
          isDark ? "border-white/10" : "border-slate-200"
        }`}
      >
        {/* ==================== 1. KOLON (SOL): FİLTRELER & KİŞİLER ==================== */}
        <aside
          className={`flex flex-col border-r ${
            isDark ? "border-white/10 bg-[#080808]" : "border-slate-200 bg-slate-50"
          }`}
        >
          <div className={`border-b p-3 ${isDark ? "border-white/10" : "border-slate-200"}`}>
            <div className={`mb-2 px-2 text-[10px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              ⭐ FAVORİLER & FİLTRELER
            </div>
            <div className="space-y-1 text-xs">
              <button
                type="button"
                onClick={() => setActiveContactFilter("all")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 transition ${
                  activeContactFilter === "all"
                    ? isDark
                      ? "bg-white/[0.08] font-bold text-white shadow-sm"
                      : "bg-slate-200 font-bold text-slate-900 shadow-sm"
                    : isDark
                    ? "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>📥</span>
                  <span>Tüm Mesajlar</span>
                </div>
                <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                  isDark ? "bg-white/10 text-slate-300" : "bg-slate-300 text-slate-800"
                }`}>
                  {groupedContacts.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveContactFilter("unread")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 transition ${
                  activeContactFilter === "unread"
                    ? "bg-yellow-500/20 font-bold text-yellow-300"
                    : isDark
                    ? "text-slate-400 hover:bg-white/[0.04]"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>⏳</span>
                  <span>Yanıt Bekleyenler</span>
                </div>
                <span className="rounded-md bg-yellow-500/30 px-1.5 py-0.5 text-[11px] font-black text-yellow-300">
                  {groupedContacts.filter((c) => c.unreadCount > 0).length}
                </span>
              </button>
            </div>
          </div>

          <div className={`flex items-center justify-between border-b px-4 py-2.5 text-[10px] font-black uppercase tracking-wider ${
            isDark ? "border-white/10 text-slate-400" : "border-slate-200 text-slate-600"
          }`}>
            <span>👥 {userRole === "publisher" ? "YÖNETİCİ & DESTEK" : "KULLANICILARIMIZ"}</span>
            <span className="font-mono text-slate-400">{filteredContacts.length} Kişi</span>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
            {filteredContacts.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-500">Kişi bulunamadı.</div>
            ) : (
              filteredContacts.map((c) => {
                const isSelected = c.key === selectedContactKey;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setSelectedContactKey(c.key)}
                    className={`group relative flex w-full flex-col rounded-2xl border p-3 text-left transition-all ${
                      isSelected
                        ? "border-yellow-500/80 bg-gradient-to-r from-yellow-500/15 via-yellow-500/5 to-transparent shadow-md ring-1 ring-yellow-500/50"
                        : isDark
                        ? "border-white/5 bg-white/[0.015] hover:border-white/20 hover:bg-white/[0.04]"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-yellow-500/30 bg-gradient-to-br from-yellow-500/20 to-black font-black text-xs text-yellow-300 shadow-sm">
                          {getInitial(c.name)}
                        </div>
                        <div className="min-w-0">
                          <div className={`truncate text-xs font-black ${
                            isSelected ? "text-yellow-400" : isDark ? "text-white" : "text-slate-900"
                          }`}>
                            {c.name}
                          </div>
                          <div className="truncate text-[10px] text-slate-400">
                            @{c.username} {c.hiddenId !== "-" ? `• ID: ${c.hiddenId}` : ""}
                          </div>
                        </div>
                      </div>

                      {c.unreadCount > 0 && (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-yellow-500 text-[10px] font-black text-black animate-pulse">
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* ==================== 2. KOLON (ORTA): SEÇİLİ KİŞİNİN KONULARI ==================== */}
        <section
          className={`flex flex-col border-r ${
            isDark ? "border-white/10 bg-[#0a0a0c]" : "border-slate-200 bg-white"
          }`}
        >
          <div className={`border-b p-3.5 ${isDark ? "border-white/10" : "border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-black uppercase text-yellow-400 truncate">
                {selectedContactGroup?.name || "Kişi Seçilmedi"}
              </div>
              <button
                type="button"
                onClick={() => {
                  setModalReceiverId(selectedContactGroup?.id || "");
                  setShowNewTopicModal(true);
                }}
                className="text-[11px] font-black text-yellow-500 hover:underline"
              >
                + Yeni Konu
              </button>
            </div>

            <div className="mt-2.5 flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveTopicFilter("all")}
                  className={`rounded-lg px-2.5 py-1 font-bold ${
                    activeTopicFilter === "all" ? "bg-yellow-500 text-black" : "text-slate-400 hover:text-white"
                  }`}
                >
                  Tümü ({selectedContactGroup?.topics?.length || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTopicFilter("unread")}
                  className={`rounded-lg px-2.5 py-1 font-bold ${
                    activeTopicFilter === "unread" ? "bg-yellow-500 text-black" : "text-slate-400 hover:text-white"
                  }`}
                >
                  Okunmamış ({selectedContactGroup?.topics?.filter((t) => t.unreadCount > 0).length || 0})
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
            {filteredTopics.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">
                Bu kişiye ait konu bulunmuyor.
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setModalReceiverId(selectedContactGroup?.id || "");
                      setShowNewTopicModal(true);
                    }}
                    className="font-bold text-yellow-400 underline"
                  >
                    + Yeni Konu Başlat
                  </button>
                </div>
              </div>
            ) : (
              filteredTopics.map((top) => {
                const isSelected = top.key === selectedTopicKey;
                return (
                  <button
                    key={top.key}
                    type="button"
                    onClick={() => setSelectedTopicKey(top.key)}
                    className={`flex w-full flex-col rounded-xl border p-3 text-left transition ${
                      isSelected
                        ? "border-yellow-500 bg-yellow-500/10 shadow-sm"
                        : isDark
                        ? "border-white/5 bg-white/[0.015] hover:border-white/20"
                        : "border-slate-200 bg-slate-50 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[10px] font-bold text-yellow-400">{top.category}</span>
                      <span className="text-[10px] text-slate-400">{formatMessageDate(top.lastDate)}</span>
                    </div>
                    <div className={`mt-1 text-xs font-black truncate ${isSelected ? "text-yellow-300" : isDark ? "text-white" : "text-slate-900"}`}>
                      {top.subject}
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400">
                      <span>{top.messages.length} mesaj</span>
                      <span className={`font-bold ${top.status === "Yanıt Bekliyor" ? "text-yellow-400" : "text-emerald-400"}`}>
                        {top.status}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </section>

        {/* ==================== 3. KOLON (SAĞ): MESAJ AKIŞI & CEVAP YAZMA ==================== */}
        <main className={`flex flex-col ${isDark ? "bg-[#0c0c0e]" : "bg-slate-50/50"}`}>
          <div className={`flex items-center justify-between border-b px-5 py-3.5 ${
            isDark ? "border-white/10 bg-[#121215]" : "border-slate-200 bg-white"
          }`}>
            <div>
              <div className="text-sm font-black text-white">{selectedTopic?.subject || "Konu Başlığı"}</div>
              <div className="text-[10px] text-slate-400">{selectedTopic?.category || "Genel Kategori"}</div>
            </div>
            {userRole === "admin" && (
              <div className="flex items-center gap-1.5 text-xs font-bold">
                <span className="text-[10px] text-slate-400">Rol:</span>
                <select
                  value={composerRole}
                  onChange={(e) => setComposerRole(e.target.value)}
                  className="rounded-lg border border-yellow-500/40 bg-black px-2 py-1 text-xs font-bold text-yellow-300 outline-none"
                >
                  <option value="admin">👑 Ajans Müdahalesi</option>
                  <option value="internal">🔒 Dahili Not</option>
                  <option value="standard">Standart</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-3.5 custom-scrollbar">
            {threadMessages.length === 0 ? (
              <div className="py-12 text-center text-xs text-slate-500">
                Henüz mesaj bulunmuyor. Aşağıdan yanıt yazarak görüşmeyi başlatabilirsiniz.
              </div>
            ) : (
              threadMessages.map((msg, idx) => {
                const isMe = Number(msg.sender_id) === Number(currentUser?.id);
                return (
                  <div key={idx} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                    <div className="mb-1 flex items-center gap-1.5 text-[10px] text-slate-400">
                      <span className="font-bold">{isMe ? "Sen" : msg.senderName || msg.senderUsername || "Kullanıcı"}</span>
                      <span>•</span>
                      <span>{formatMessageDate(msg.created_at)}</span>
                    </div>
                    <div
                      className={`max-w-[85%] rounded-2xl p-3.5 text-xs leading-5 shadow-md ${
                        isMe
                          ? "bg-gradient-to-r from-yellow-500 to-amber-500 font-medium text-black rounded-tr-none"
                          : isDark
                          ? "border border-white/10 bg-[#16161a] text-white rounded-tl-none"
                          : "border border-slate-200 bg-white text-slate-900 rounded-tl-none"
                      }`}
                    >
                      {msg.message}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className={`border-t p-3.5 ${isDark ? "border-white/10 bg-[#121215]" : "border-slate-200 bg-white"}`}>
            <div className="flex gap-2">
              <textarea
                id="unifiedReplyInput"
                rows={2}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
                placeholder="Mesajınızı yazınız... (Göndermek için Ctrl+Enter)"
                className={`flex-1 resize-none rounded-xl border p-2.5 text-xs outline-none transition ${
                  isDark
                    ? "border-white/10 bg-black/60 text-white placeholder-white/40 focus:border-yellow-500/50"
                    : "border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-yellow-500"
                }`}
              />
              <button
                type="button"
                disabled={isSending}
                onClick={handleSendReply}
                className="flex items-center justify-center rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-5 text-xs font-black text-black shadow-md transition hover:brightness-110 disabled:opacity-50"
              >
                {isSending ? "..." : "Gönder ▸"}
              </button>
            </div>
          </div>
        </main>
      </div>

      {showNewTopicModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-3xl border border-yellow-500/30 bg-[#101014] p-6 text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-base font-black text-yellow-400">＋ Yeni Konu / Talep Aç</h3>
              <button type="button" onClick={() => setShowNewTopicModal(false)} className="text-white/60 hover:text-white">✕</button>
            </div>

            <div className="mt-4 space-y-3 text-xs">
              <div>
                <label className="font-bold text-white/70">Alıcı Seçiniz</label>
                <select
                  value={modalReceiverId}
                  onChange={(e) => setModalReceiverId(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/60 p-2.5 text-xs font-bold text-white outline-none"
                >
                  {activeContactsList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName || c.name || c.username} (@{c.username})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-bold text-white/70">Kategori</label>
                <select
                  value={modalCategory}
                  onChange={(e) => setModalCategory(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/60 p-2.5 text-xs font-bold text-white outline-none"
                >
                  <option value="💎 ELMAS & HEDEF">💎 ELMAS & HEDEF</option>
                  <option value="📦 ÖDÜL & KARGO">📦 ÖDÜL & KARGO</option>
                  <option value="🎬 VİDEO KURGU & EDİT">🎬 VİDEO KURGU & EDİT</option>
                  <option value="⚔️ PK & ETKİNLİK">⚔️ PK & ETKİNLİK</option>
                  <option value="⚙️ TEKNİK DESTEK">⚙️ TEKNİK DESTEK</option>
                  <option value="💬 GENEL">💬 GENEL</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-white/70">Konu Başlığı</label>
                <input
                  type="text"
                  value={modalSubject}
                  onChange={(e) => setModalSubject(e.target.value)}
                  placeholder="Örn: Haftalık PK Planlaması"
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/60 p-2.5 text-xs text-white outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-white/70">Mesajınız</label>
                <textarea
                  rows={4}
                  value={modalMessage}
                  onChange={(e) => setModalMessage(e.target.value)}
                  placeholder="Mesajınızı detaylı şekilde yazınız..."
                  className="mt-1 w-full rounded-xl border border-white/10 bg-black/60 p-2.5 text-xs text-white outline-none resize-none"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2 border-t border-white/10 pt-3">
              <button
                type="button"
                onClick={() => setShowNewTopicModal(false)}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-white/60 hover:text-white"
              >
                İptal
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={handleCreateNewTopic}
                className="rounded-xl bg-gradient-to-r from-yellow-400 to-amber-500 px-5 py-2 text-xs font-black text-black shadow-lg hover:brightness-110"
              >
                {isSending ? "Oluşturuluyor..." : "Konuyu Başlat"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
